import React from 'react'
import { ArrowDown, ArrowLeftRight, ArrowUp } from 'lucide-react'
import { formatActivityDateCompact, roleChangePhrase } from '../../../utils/format'
import { PersonActivityRow } from '../../../utils/personRecentActivity'
import { Badge, CountryFlagIcon } from '../../ui'
import EntityProfileLink from '../../watchlist/EntityProfileLink'

/** Compact ownership % badge (compact label + full precision on hover). */
const StakeBadge: React.FC<{ compact: string; full?: string | null }> = ({
  compact,
  full,
}) => (
  <Badge
    tone="muted"
    title={full ?? undefined}
    className="shrink-0 whitespace-nowrap tabular-nums font-normal px-1.5 py-px text-[11px] leading-tight"
  >
    {compact}
  </Badge>
)

/** Renders one or more activity rows (shared by overview strip and Aktivitet tab). */
export const PersonActivityRowList: React.FC<{ rows: PersonActivityRow[] }> = ({
  rows,
}) => (
  <>
    {rows.map((row, idx) => (
      <ActivityRow key={`${row.kind}-${row.date}-${row.companyCvr}-${idx}`} row={row} />
    ))}
  </>
)

const ActivityRow: React.FC<{ row: PersonActivityRow }> = ({ row }) => {
  const dateLabel = formatActivityDateCompact(row.date)
  const Icon =
    row.kind === 'in' ? ArrowDown : row.kind === 'out' ? ArrowUp : ArrowLeftRight
  const iconLabel =
    row.kind === 'in' ? 'Indtrådt' : row.kind === 'out' ? 'Udtrådt' : 'Ændret ejerskab'
  // Direction is carried by the arrow and its label; red stays for flags.
  const iconClass = 'text-ink-500'

  return (
    <li className="py-1.5 border-b border-rule-soft last:border-b-0 text-[13px]">
      <div className="flex items-center gap-2 min-w-0">
        <span className="tabular-nums text-xs text-ink-500 shrink-0 whitespace-nowrap text-left mr-3">
          {dateLabel}
        </span>
        <span title={iconLabel} aria-label={iconLabel} className="inline-flex shrink-0">
          <Icon aria-hidden className={`h-3 w-3 ${iconClass}`} />
        </span>
        <ActivityDescription row={row} />
      </div>
    </li>
  )
}

const ActivityDescription: React.FC<{ row: PersonActivityRow }> = ({ row }) => {
  const companyLink = row.companyCvr ? (
    <EntityProfileLink
      to={`/company/${row.companyCvr}`}
      entityType="company"
      entityId={String(row.companyCvr)}
      title={row.companyName}
      className="font-medium text-ink hover:underline decoration-signal underline-offset-2 truncate min-w-0"
    >
      {row.companyName}
    </EntityProfileLink>
  ) : (
    <span className="font-medium truncate min-w-0" title={row.companyName}>
      {row.companyName}
    </span>
  )
  const companyWithFlag = (
    <span className="inline-flex items-baseline gap-1 min-w-0">
      {companyLink}
      {row.companyCountryCode && (
        <CountryFlagIcon code={row.companyCountryCode} />
      )}
    </span>
  )

  if (row.kind === 'stake_change') {
    return (
      <p className="flex-1 min-w-0 flex items-baseline gap-1 text-xs text-ink-700">
        <span className="shrink-0">ændrede ejerskab</span>
        {row.previousStakeLabel && row.stakeLabel ? (
          <>
            <StakeBadge
              compact={row.previousStakeLabel}
              full={row.previousStakeTitle}
            />
            <span className="text-ink-500 shrink-0" aria-hidden>
              →
            </span>
            <StakeBadge compact={row.stakeLabel} full={row.stakeTitle} />
          </>
        ) : (
          row.stakeLabel && (
            <>
              <span className="shrink-0">til</span>
              <StakeBadge compact={row.stakeLabel} full={row.stakeTitle} />
            </>
          )
        )}
        <span className="shrink-0">i</span>
        {companyWithFlag}
      </p>
    )
  }

  return (
    <p className="flex-1 min-w-0 flex items-baseline gap-1 text-xs text-ink-700">
      <span className="shrink-0">
        {roleChangePhrase(row.kind === 'in' ? 'in' : 'out', row.roleLabel)}
        {row.stakeLabel && (
          <>
            {' '}
            <StakeBadge compact={row.stakeLabel} full={row.stakeTitle} />
          </>
        )}
        {' '}
        i
      </span>
      {companyWithFlag}
    </p>
  )
}
